import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Tasks.module.scss';
import {
  IconPlus,
  IconListNumbers,
  IconLayoutBoard,
  IconFilterFilled,
  IconSearch,
  IconX,
  IconChevronDown
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';

import QIcon from '../../assets/images/QIcon.svg';
import { ProjectDto, Status, PermissionKey } from '../../api/model';
import { TaskDto } from '../../api/model';
import { useGetTask, usePutTaskId } from '../../api/task-api/task-api';
import { useNotifications } from '../../hooks/useNotifications';
import { useProjectTasks } from '../../hooks/useProjectTasks';
import {getPhaseDisplayName} from '../../utils/phaseUtils';
import { useTenantUsers } from '../../hooks/useTenantUsers';
import { useCurrentUserPermissions } from '../../hooks/useCurrentUserPermissions';
import CreateTaskModal from './CreateTaskModal';
import EditTaskModal from './EditTaskModal';
import { useProjectDeliverables } from '../../hooks/useProjectDeliverables';
import { useProjectPhases } from '../../hooks/useProjectPhases';

interface TasksProps {
  project?: ProjectDto;
  onDataRefresh?: () => Promise<any>;
  taskIdToOpen?: number | null;
  onTaskOpened?: () => void;
}

interface TeamMember {
  id: string;
  name: string;
  avatarUrl: string;
}

interface TaskItem {
  id: string;
  taskId: number;
  name: string;
  description: string;
  assignedPerson?: string;
  status: 'backlog' | 'ready' | 'inProgress' | 'inReview' | 'done';
  progress?: number;
  deliverObjectId?: number;
  created?: string;
  modified?: string;
}

const Tasks: React.FC<TasksProps> = ({ project, onDataRefresh, taskIdToOpen, onTaskOpened }) => {
  const { t } = useTranslation('tasks');
  // Fetch tasks filtered 
  const { 
    tasks: allTasks, 
    isLoading: isLoadingTasks, 
    error: tasksError
  } = useProjectTasks(project);
  const { 
    deliverables: allDeliverables, 
    isLoading: isLoadingDeliverables 
  } = useProjectDeliverables(project);
  const { data: phases } = useProjectPhases(project?.id);
  const { namesByIds: assigneeNames } = useTenantUsers();
  const { can } = useCurrentUserPermissions();
  const canCreateTask = can(PermissionKey.TASKS, 'CREATE');
  const queryClient = useQueryClient();

  // Load saved view preference
  const [currentView, setCurrentView] = useState<'list' | 'board'>(() => {
    const savedView = localStorage.getItem('tasksView');
    return (savedView === 'list' || savedView === 'board') ? savedView : 'list';
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem('tasksItemsPerPage');
    return saved ? parseInt(saved, 10) || 8 : 8;
  });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedTaskForEdit, setSelectedTaskForEdit] = useState<TaskDto | null>(null);
  const [draggedTask, setDraggedTask] = useState<TaskItem | null>(null);

  // Filter states.
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [taskNameFilter, setTaskNameFilter] = useState('');
  const [deliverableNameFilter, setDeliverableNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
    const [assignedPersonFilter, setAssignedPersonFilter] = useState<string[]>([]);
    const [isAssignedPersonDropdownOpen, setIsAssignedPersonDropdownOpen] = useState(false);
  const filterPanelRef = useRef<HTMLDivElement>(null);

  // Hooks for notifications
  const updateTaskMutation = usePutTaskId();
  const { notifySuccess, notifyError } = useNotifications();

  // Save view preference
  useEffect(() => {
    localStorage.setItem('tasksView', currentView);
  }, [currentView]);

  // Close filter panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(event.target as Node)
      ) {
        setIsFilterPanelOpen(false);
        setIsStatusDropdownOpen(false);
          setIsAssignedPersonDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

    const hasActiveFilters = !!(taskNameFilter || deliverableNameFilter || statusFilter.length > 0 || assignedPersonFilter.length > 0);

  // open task detail modal
  useEffect(() => {
    if (taskIdToOpen && allTasks) {
      const taskToOpen = allTasks.find(t => t.id === taskIdToOpen);
      if (taskToOpen) {
        setSelectedTaskForEdit(taskToOpen);
        if (onTaskOpened) {
          onTaskOpened();
        }
      }
    }
  }, [taskIdToOpen, allTasks, onTaskOpened]);


  const mapStatusToBoardStatus = (status?: Status): 'backlog' | 'ready' | 'inProgress' | 'inReview' | 'done' => {
    switch (status) {
      case Status.BACKLOG:
        return 'backlog';
      case Status.PLANNED:
        return 'ready';
      case Status.IN_PROGRESS:
        return 'inProgress';
      case Status.DELAYED:
        return 'inReview';
      case Status.FINISHED:
        return 'done';
      case Status.CANCELLED:
        return 'backlog';
      default:
        return 'backlog';
    }
  };

  // Get display label
  const getStatusDisplayLabel = (status: 'backlog' | 'ready' | 'inProgress' | 'inReview' | 'done'): string => {
    const labels = {
      'backlog': t('status.backlog'),
      'ready': t('status.ready'),
      'inProgress': t('status.inProgress'),
      'inReview': t('status.inReview'),
      'done': t('status.done')
    };
    return labels[status];
  };

  // Convert task data
  const deliverableDateMap = useMemo(() => {
    const map = new Map<number, string>();
    if (allDeliverables) {
      allDeliverables.forEach(deliverable => {
        if (deliverable.id !== undefined && deliverable.startDate) {
          map.set(deliverable.id, deliverable.startDate.toString());
        }
      });
    }
    return map;
  }, [allDeliverables]);

  const processedTaskData = useMemo((): TaskItem[] => {
    if (!allTasks) return [];

    const items = allTasks.map((task) => {
      return {
        id: `T${task.id || 0}`,
        taskId: task.id || 0,
        name: task.name || t('loading.unnamed'),
        description: task.description || t('loading.noDescription'),
        // resolve the assigned user id to a display name for board/list rendering
        assignedPerson: task.assignedUserIds?.length ? assigneeNames(task.assignedUserIds) : undefined,
        status: mapStatusToBoardStatus(task.status),
        progress: task.progress,
        deliverObjectId: task.deliverObjectId,
        created: task.created?.toString(),
        modified: task.modified?.toString()
      };
    });

    items.sort((a, b) => {
      const dateA = a.deliverObjectId ? deliverableDateMap.get(a.deliverObjectId) : undefined;
      const dateB = b.deliverObjectId ? deliverableDateMap.get(b.deliverObjectId) : undefined;
      if (!dateA && !dateB) return (a.taskId) - (b.taskId);
      if (!dateA) return 1;
      if (!dateB) return -1;
      const cmp = dateA.localeCompare(dateB);
      return cmp !== 0 ? cmp : (a.taskId) - (b.taskId);
    });

    return items;
  }, [allTasks, deliverableDateMap, assigneeNames]);

  const taskData = processedTaskData;

  // Create deliverable map
  const deliverableMap = useMemo(() => {
    const map = new Map<number, string>();
    if (allDeliverables) {
      allDeliverables.forEach(deliverable => {
        if (deliverable.id !== undefined && deliverable.name) {
          map.set(deliverable.id, deliverable.name);
        }
      });
    }
    return map;
  }, [allDeliverables]);

  const phaseNameMap = useMemo(() => {
    const map = new Map<number, string>();
    if (allDeliverables && phases) {
      allDeliverables.forEach(del => {
        if (del.id !== undefined && del.phaseId !== undefined) {
          const phase = phases.find(p => p.id === del.phaseId);
          if (phase) {
              map.set(del.id, getPhaseDisplayName(phase));
          }
        }
      });
    }
    return map;
  }, [allDeliverables, phases]);

    const uniqueAssignedPersons = useMemo(() => {
        const persons = new Set<string>();
        taskData.forEach(task => {
            if (task.assignedPerson) persons.add(task.assignedPerson);
        });
        return Array.from(persons).sort();
    }, [taskData]);

  // Apply filters
  const filteredTaskData = useMemo(() => {
    return taskData.filter(task => {
      // Filter by task name
      if (taskNameFilter && !task.name.toLowerCase().includes(taskNameFilter.toLowerCase())) {
        return false;
      }
      // Filter by deliverable name
      if (deliverableNameFilter) {
        const delName = task.deliverObjectId ? deliverableMap.get(task.deliverObjectId) || '' : '';
        if (!delName.toLowerCase().includes(deliverableNameFilter.toLowerCase())) {
          return false;
        }
      }
      // Filter by status
      if (statusFilter.length > 0 && !statusFilter.includes(task.status)) {
        return false;
      }
        // Filter by assigned person
        if (assignedPersonFilter.length > 0 && !assignedPersonFilter.includes(task.assignedPerson || '')) {
            return false;
      }
      return true;
    });
  }, [taskData, taskNameFilter, deliverableNameFilter, statusFilter, assignedPersonFilter, deliverableMap]);

  // find total pages
  const totalPages = Math.ceil(filteredTaskData.length / itemsPerPage);

  const currentItems = filteredTaskData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Pagination
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    localStorage.setItem('tasksItemsPerPage', newItemsPerPage.toString());
    setCurrentPage(1);
  };

  const handleTaskCreated = async () => {
    setIsCreateModalOpen(false);

    // refetch trigger
    await queryClient.invalidateQueries({ 
      queryKey: ['/task']
    });
  };

  const handleEditTask = (task: TaskItem) => {
    const originalTask = allTasks?.find(t => t.id === task.taskId);
    if (originalTask) {
      setSelectedTaskForEdit(originalTask);
    }
  };

  const handleTaskUpdated = async () => {
    setSelectedTaskForEdit(null);

    // trigger refetch
      await queryClient.invalidateQueries({queryKey: ['/task']});
      await queryClient.invalidateQueries({ predicate: (query) => typeof query.queryKey[0] === 'string' && (query.queryKey[0] as string).startsWith('/checkpoint') });
  };

  const handleTaskDeleted = async () => {
    setSelectedTaskForEdit(null);

    // trigger refetch
      await queryClient.invalidateQueries({queryKey: ['/task']});
      await queryClient.invalidateQueries({ predicate: (query) => typeof query.queryKey[0] === 'string' && (query.queryKey[0] as string).startsWith('/checkpoint') });
  };

  // Drag drop handlings
  const handleDragStart = (e: React.DragEvent, task: TaskItem) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedTask(null);
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, newStatus: 'backlog' | 'ready' | 'inProgress' | 'inReview' | 'done') => {
    e.preventDefault();
    
    if (!draggedTask) return;

    // if status no change, do nothing
    if (draggedTask.status === newStatus) {
      setDraggedTask(null);
      return;
    }

    // Prevent multiple simultaneous updates
    if (updateTaskMutation.isPending) {
      return;
    }

    const taskId = draggedTask.taskId;
    const taskName = draggedTask.name;

    try {
      const statusMap: Record<'backlog' | 'ready' | 'inProgress' | 'inReview' | 'done', Status> = {
        'backlog': Status.BACKLOG,
        'ready': Status.PLANNED,
        'inProgress': Status.IN_PROGRESS,
        'inReview': Status.DELAYED,
        'done': Status.FINISHED
      };

      // Map status to progress
      const progressMap: Record<'backlog'| 'ready' | 'inProgress' | 'inReview' | 'done', number> = {
        'backlog': 0,
        'ready': 25,
        'inProgress': 50,
        'inReview': 75,
        'done': 100
      };

      const apiStatus = statusMap[newStatus];
      const progress = progressMap[newStatus];

      // Find original task
      const originalTask = allTasks?.find(t => t.id === taskId);

      if (!originalTask) {
        notifyError(t('status.backlog'), t('loading.taskNotFound'));
        setDraggedTask(null);
        return;
      }

      // Update the task with new status and progress
      const updatedTaskData: TaskDto = {
        id: taskId,
        name: originalTask.name || taskName,
        description: originalTask.description,
        status: apiStatus,
        progress: progress,
        deliverObjectId: originalTask.deliverObjectId
      };

      setDraggedTask(null);

      await updateTaskMutation.mutateAsync({
        id: taskId,
        data: updatedTaskData
      });

      // invalidate queries to refetch
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['/task'] }),
        queryClient.invalidateQueries({ queryKey: ['/project'] }),
        queryClient.invalidateQueries({ queryKey: ['projects'] }),
        queryClient.invalidateQueries({ predicate: (query) => typeof query.queryKey[0] === 'string' && (query.queryKey[0] as string).startsWith('/intelligence') }),
          queryClient.invalidateQueries({ predicate: (query) => typeof query.queryKey[0] === 'string' && (query.queryKey[0] as string).startsWith('/checkpoint') }),
        queryClient.invalidateQueries({ predicate: (query) => typeof query.queryKey[0] === 'string' && (query.queryKey[0] as string).includes('/deliverobject') })
      ]);

      notifySuccess('Task Status Updated', `"${taskName}" moved to ${getStatusDisplayLabel(newStatus)}`);
    } catch (error) {
      console.error('Error updating task status:', error);
      notifyError('Task Update Failed', error);

      // Refetch
      queryClient.refetchQueries({ queryKey: ['/task'] });
    }
  };

  return (
    <>
      <div className={styles.planHeader}>
        <div className={styles.planHeaderContent}>
          <h2 className={styles.planTitle}>{t('title')}</h2>
          <p className={styles.planDescription}>{t('description')}</p>
        </div>
        <div className={styles.planActions}>
          <div style={{ position: 'relative' }}>
            <button
              className={`${styles.filterButton} ${isFilterPanelOpen ? styles.active : ''} ${
                hasActiveFilters ? styles.filtersApplied : ''
              }`}
              onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
              aria-label={t('aria.filterTasks')}
            >
              <IconFilterFilled />
              <span className={styles.filterButtonText}>{t('filter')}</span>
              {hasActiveFilters && <span className={styles.filterBadge}></span>}
            </button>

            {isFilterPanelOpen && (
              <div className={styles.filterPanel} ref={filterPanelRef}>
                <div className={styles.filterSearchWrapper}>
                  <div className={styles.searchInput}>
                    <IconSearch size={16} className={styles.searchIcon} />
                    <input
                      type="text"
                      placeholder={t('searchTask')}
                      value={taskNameFilter}
                      onChange={(e) => { setTaskNameFilter(e.target.value); setCurrentPage(1); }}
                      className={styles.filterInput}
                    />
                    {taskNameFilter && (
                      <button
                        className={styles.clearButton}
                        onClick={() => { setTaskNameFilter(''); setCurrentPage(1); }}
                        aria-label={t('aria.clearTaskSearch')}
                      >
                        <IconX size={14} />
                      </button>
                    )}
                  </div>
                </div>

                <div className={styles.filterSearchWrapper}>
                  <div className={styles.searchInput}>
                    <IconSearch size={16} className={styles.searchIcon} />
                    <input
                      type="text"
                      placeholder={t('searchDeliverable')}
                      value={deliverableNameFilter}
                      onChange={(e) => { setDeliverableNameFilter(e.target.value); setCurrentPage(1); }}
                      className={styles.filterInput}
                    />
                    {deliverableNameFilter && (
                      <button
                        className={styles.clearButton}
                        onClick={() => { setDeliverableNameFilter(''); setCurrentPage(1); }}
                        aria-label={t('aria.clearDeliverableSearch')}
                      >
                        <IconX size={14} />
                      </button>
                    )}
                  </div>
                </div>

                <div className={styles.filterCriticalityWrapper}>
                  <div className={styles.dropdownContainer}>
                    <button
                      className={styles.dropdownButton}
                      onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                    >
                      {statusFilter.length > 0
                        ? `${statusFilter.length} status selected`
                        : t('filter')}
                      <IconChevronDown size={16} className={`${styles.dropdownIcon} ${isStatusDropdownOpen ? styles.rotate : ''}`} />
                    </button>

                    {isStatusDropdownOpen && (
                      <div className={styles.dropdown}>
                        {[
                          { key: 'backlog', label: t('status.backlog'), dotClass: styles.backlogDot },
                          { key: 'ready', label: t('status.ready'), dotClass: styles.readyDot },
                          { key: 'inProgress', label: t('status.inProgress'), dotClass: styles.inProgressDot },
                          { key: 'inReview', label: t('status.inReview'), dotClass: styles.inReviewDot },
                          { key: 'done', label: t('status.done'), dotClass: styles.doneDot },
                        ].map(({ key, label, dotClass }) => (
                          <div key={key} className={styles.dropdownItem}>
                            <label className={styles.checkboxLabel}>
                              <input
                                type="checkbox"
                                checked={statusFilter.includes(key)}
                                onChange={() => { handleStatusFilterChange(key); setCurrentPage(1); }}
                                className={styles.checkbox}
                              />
                              <span className={`${styles.criticalityDot} ${dotClass}`}></span>
                              {label}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.filterCriticalityWrapper}>
                  <div className={styles.dropdownContainer}>
                    <button
                      className={styles.dropdownButton}
                      onClick={() => setIsAssignedPersonDropdownOpen(!isAssignedPersonDropdownOpen)}
                    >
                      {assignedPersonFilter.length > 0
                        ? `${assignedPersonFilter.length} person selected`
                        : t('filter')}
                      <IconChevronDown size={16}
                        className={`${styles.dropdownIcon} ${isAssignedPersonDropdownOpen ? styles.rotate : ''}`} />
                    </button>

                    {isAssignedPersonDropdownOpen && (
                      <div className={styles.dropdown}>
                        {uniqueAssignedPersons.length === 0 ? (
                          <div className={styles.dropdownItem}
                            style={{ color: '#888', fontStyle: 'italic' }}>{t('emptyState.noPersonsAssigned')}</div>
                        ) : (
                          uniqueAssignedPersons.map(person => (
                            <div key={person} className={styles.dropdownItem}>
                              <label className={styles.checkboxLabel}>
                                <input
                                  type="checkbox"
                                  checked={assignedPersonFilter.includes(person)}
                                  onChange={() => {
                                    setAssignedPersonFilter(prev =>
                                      prev.includes(person) ? prev.filter(p => p !== person) : [...prev, person]
                                    );
                                    setCurrentPage(1);
                                  }}
                                  className={styles.checkbox}
                                />
                                {person}
                              </label>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={styles.viewToggle}>
            <button
              className={`${styles.viewToggleButton} ${currentView === 'list' ? styles.viewToggleButtonActive : ''}`}
              onClick={() => setCurrentView('list')}
            >
              <IconListNumbers size={18} />
              {t('viewToggle.list')}
            </button>
            <button
              className={`${styles.viewToggleButton} ${currentView === 'board' ? styles.viewToggleButtonActive : ''}`}
              onClick={() => setCurrentView('board')}
            >
              <IconLayoutBoard size={18} />
              {t('viewToggle.board')}
            </button>
          </div>
          {canCreateTask && (
            <button
              className={styles.createTaskButton}
              onClick={() => setIsCreateModalOpen(true)}
            >
              <IconPlus size={18} />
              {t('createButton')}
            </button>
          )}
        </div>
      </div>

      <div className={styles.mainContainer}>
        <div className={styles.tableWrapper} style={{ width: '100%' }}>
          {isLoadingTasks ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateContent}>
                <h3>{t('loading.tasks')}</h3>
                <p>{t('loading.description')}</p>
              </div>
            </div>
          ) : tasksError ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateContent}>
                <h3>{t('error.title')}</h3>
                <p>{t('error.description')}</p>
                <button
                  className={styles.createTaskButton}
                  onClick={() => queryClient.refetchQueries({ queryKey: ['/task'] })}
                  style={{ marginTop: '16px' }}
                >
                  {t('error.retry')}
                </button>
              </div>
            </div>
          ) : taskData.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateContent}>
                <h3>{t('emptyState.title')}</h3>
                <p>{t('emptyState.noTasks')}</p>
              </div>
            </div>
          ) : filteredTaskData.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateContent}>
                <h3>{t('emptyState.noMatchTitle')}</h3>
                <p>{t('emptyState.noMatch')}</p>
              </div>
            </div>
          ) : currentView === 'list' ? (
            <div className={styles.listViewWrapper}>
              <div className={styles.tableScrollArea}>
                <div className={styles.table}>
                  <div className={styles.content}>
                <div className={styles.column}>
                  <div className={styles.headerCell}>
                    <div className={styles.tableHeader}>
                      <span className={styles.headerText} title={t('table.id')}>{t('table.id')}</span>
                    </div>
                  </div>
                  {currentItems.map((item) => (
                    <div 
                      key={item.id} 
                      className={styles.tableCell}
                      onClick={() => handleEditTask(item)}
                    >
                      <div className={styles.textContent}>
                        <span className={styles.text}>{item.id}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={styles.column}>
                  <div className={styles.headerCell}>
                    <div className={styles.tableHeader}>
                      <span className={styles.headerText} title={t('table.name')}>{t('table.name')}</span>
                    </div>
                  </div>
                  {currentItems.map((item) => (
                    <div 
                      key={item.id} 
                      className={styles.tableCell}
                      onClick={() => handleEditTask(item)}
                    >
                      <div className={styles.textContent}>
                        <span className={styles.truncatedText} title={item.name}>
                          {item.name.length > 25 
                            ? `${item.name.substring(0, 25)}...` 
                            : item.name}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={styles.column}>
                  <div className={styles.headerCell}>
                    <div className={styles.tableHeader}>
                      <span className={styles.headerText} title={t('table.phase')}>{t('table.phase')}</span>
                    </div>
                  </div>
                  {currentItems.map((item) => (
                    <div
                      key={item.id}
                      className={styles.tableCell}
                      onClick={() => handleEditTask(item)}
                    >
                      <div className={styles.textContent}>
                        <span className={styles.truncatedText} title={item.deliverObjectId ? phaseNameMap.get(item.deliverObjectId) || '—' : '—'}>
                          {item.deliverObjectId ? phaseNameMap.get(item.deliverObjectId) || '—' : '—'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={styles.column}>
                  <div className={styles.headerCell}>
                    <div className={styles.tableHeader}>
                      <span className={styles.headerText} title={t('table.deliverable')}>{t('table.deliverable')}</span>
                    </div>
                  </div>
                  {currentItems.map((item) => (
                    <div 
                      key={item.id} 
                      className={styles.tableCell}
                      onClick={() => handleEditTask(item)}
                    >
                      <div className={styles.textContent}>
                        <span className={styles.truncatedText} title={item.deliverObjectId ? deliverableMap.get(item.deliverObjectId) || `${t('table.deliverable')} ${item.deliverObjectId}` : t('table.noDeliverable')}>
                          {item.deliverObjectId ? deliverableMap.get(item.deliverObjectId)?.replaceAll('_', ' ') || `${t('table.deliverable')} ${item.deliverObjectId}` : t('table.noDeliverable')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={styles.column}>
                  <div className={styles.headerCell}>
                    <div className={styles.tableHeader}>
                      <span className={styles.headerText} title={t('table.assignedPerson')}>{t('table.assignedPerson')}</span>
                    </div>
                  </div>
                  {currentItems.map((item) => (
                    <div 
                      key={item.id} 
                      className={styles.tableCell}
                      onClick={() => handleEditTask(item)}
                    >
                      <div className={styles.textContent}>
                        <span className={styles.text}>{item.assignedPerson || t('table.notAssigned')}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={styles.column}>
                  <div className={styles.headerCell}>
                    <div className={styles.tableHeader}>
                      <span className={styles.headerText} title={t('table.description')}>{t('table.description')}</span>
                    </div>
                  </div>
                  {currentItems.map((item) => (
                    <div 
                      key={item.id} 
                      className={styles.tableCell}
                      onClick={() => handleEditTask(item)}
                    >
                      <div className={styles.textContent}>
                        <span className={styles.truncatedText} title={item.description}>
                          {item.description.length > 40 
                            ? `${item.description.substring(0, 40)}...` 
                            : item.description}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={styles.column}>
                  <div className={styles.headerCell}>
                    <div className={styles.tableHeader}>
                      <span className={styles.headerText} title={t('table.progress')}>{t('table.progress')}</span>
                    </div>
                  </div>
                  {currentItems.map((item) => (
                    <div 
                      key={item.id} 
                      className={styles.tableCell}
                      onClick={() => handleEditTask(item)}
                    >
                      <div className={styles.progressBar}>
                        <div 
                          className={`${styles.progress} ${
                            item.progress === 100 ? styles.progressFull :
                            item.progress && item.progress >= 50 ? styles.progressMedium :
                            styles.progressLow
                          }`} 
                          style={{ width: `${item.progress || 0}%` }}
                        />
                        <span className={styles.progressText}>{`${item.progress || 0}%`}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={styles.column}>
                  <div className={styles.headerCell}>
                    <div className={styles.tableHeader}>
                      <span className={styles.headerText} title={t('table.status')}>{t('table.status')}</span>
                    </div>
                  </div>
                  {currentItems.map((item) => (
                    <div
                      key={item.id}
                      className={styles.tableCell}
                      onClick={() => handleEditTask(item)}
                    >
                      <div className={styles.textContent}>
                        <span className={`${styles.statusBadge} ${styles[item.status]}`}>
                          {getStatusDisplayLabel(item.status)}
                        </span>
                      </div>
                    </div>
                  ))}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.pagination}>
                <div className={styles.paginationLeft}>
                  <span className={styles.itemsPerPageLabel}>{t('pagination.show')}</span>
                  <select
                    className={styles.itemsPerPageSelect}
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  >
                    <option value={5}>5</option>
                    <option value={8}>8</option>
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                  </select>
                  <span className={styles.itemsPerPageLabel}>{t('pagination.perPage')}</span>
                </div>

                <span className={styles.paginationDetails}>
                  {totalPages > 0 ? (
                    <>
                      {t('pagination.pageOf', { page: currentPage, total: totalPages })}
                      {filteredTaskData.length > 0 && (
                        <span> • {t('pagination.showingRange', { from: ((currentPage - 1) * itemsPerPage) + 1, to: Math.min(currentPage * itemsPerPage, filteredTaskData.length), total: filteredTaskData.length })}{hasActiveFilters ? ` ${t('pagination.filteredFrom', { total: taskData.length })}` : ''}</span>
                      )}
                    </>
                  ) : (
                    t('pagination.noResults')
                  )}
                </span>

                <div className={styles.paginationRight}>
                  <button
                    className={styles.paginationButton}
                    onClick={handlePrevPage}
                    disabled={currentPage === 1 || totalPages === 0}
                    aria-label={t('pagination.prevAria')}
                  >
                    {t('pagination.previous')}
                  </button>
                  <button
                    className={styles.paginationButton}
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages || totalPages === 0}
                    aria-label={t('pagination.nextAria')}
                  >
                    {t('pagination.next')}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.boardContainer}>
              <div
                className={styles.boardColumn}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, 'backlog')}
              >
                <div className={styles.columnHeader}>
                  <div className={styles.columnTitle}>{t('status.backlog')}</div>
                </div>
                <div className={styles.columnContent}>
                  {filteredTaskData.filter(t => t.status === 'backlog').map(task => (
                    <div
                      key={task.id}
                      className={styles.taskCard}
                      draggable
                      onDragStart={(e) => handleDragStart(e, task)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleEditTask(task)}
                      style={{ cursor: 'grab' }}
                    >
                      <div className={styles.taskCardInner}>
                        <div className={styles.taskContent}>
                          <div className={styles.taskTitle}>
                            <div className={styles.taskTitleText}>{task.name.replaceAll('_', ' ')}</div>
                          </div>
                          {task.deliverObjectId && phaseNameMap.get(task.deliverObjectId) && (
                            <div className={styles.taskPhase}>{phaseNameMap.get(task.deliverObjectId)}</div>
                          )}
                          <div className={styles.taskAssignee}>
                            {t('table.assignedLabel')} {task.assignedPerson || t('table.notAssigned')}
                          </div>
                          <div className={styles.taskSummary}>{task.description.replaceAll('_', ' ')}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div 
                className={styles.boardColumn}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, 'ready')}
              >
                <div className={styles.columnHeader}>
                  <div className={styles.columnTitle}>{t('status.ready')}</div>
                </div>
                <div className={styles.columnContent}>
                  {filteredTaskData.filter(t => t.status === 'ready').map(task => (
                    <div
                      key={task.id}
                      className={styles.taskCard}
                      draggable
                      onDragStart={(e) => handleDragStart(e, task)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleEditTask(task)}
                      style={{ cursor: 'grab' }}
                    >
                      <div className={styles.taskCardInner}>
                        <div className={styles.taskContent}>
                          <div className={styles.taskTitle}>
                            <div className={styles.taskTitleText}>{task.name.replaceAll('_', ' ')}</div>
                          </div>
                          {task.deliverObjectId && phaseNameMap.get(task.deliverObjectId) && (
                            <div className={styles.taskPhase}>{phaseNameMap.get(task.deliverObjectId)}</div>
                          )}
                          <div className={styles.taskAssignee}>
                            {t('table.assignedLabel')} {task.assignedPerson || t('table.notAssigned')}
                          </div>
                          <div className={styles.taskSummary}>{task.description.replaceAll('_', ' ')}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div 
                className={styles.boardColumn}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, 'inProgress')}
              >
                <div className={styles.columnHeader}>
                  <div className={styles.columnTitle}>{t('status.inProgress')}</div>
                </div>
                <div className={styles.columnContent}>
                  {filteredTaskData.filter(t => t.status === 'inProgress').map(task => (
                    <div
                      key={task.id}
                      className={styles.taskCard}
                      draggable
                      onDragStart={(e) => handleDragStart(e, task)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleEditTask(task)}
                      style={{ cursor: 'grab' }}
                    >
                      <div className={styles.taskCardInner}>
                        <div className={styles.taskContent}>
                          <div className={styles.taskTitle}>
                            <div className={styles.taskTitleText}>{task.name.replaceAll('_', ' ')}</div>
                          </div>
                          {task.deliverObjectId && phaseNameMap.get(task.deliverObjectId) && (
                            <div className={styles.taskPhase}>{phaseNameMap.get(task.deliverObjectId)}</div>
                          )}
                          <div className={styles.taskAssignee}>
                            {t('table.assignedLabel')} {task.assignedPerson || t('table.notAssigned')}
                          </div>
                          <div className={styles.taskSummary}>{task.description.replaceAll('_', ' ')}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div 
                className={styles.boardColumn}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, 'inReview')}
              >
                <div className={styles.columnHeader}>
                  <div className={styles.columnTitle}>{t('status.inReview')}</div>
                </div>
                <div className={styles.columnContent}>
                  {filteredTaskData.filter(t => t.status === 'inReview').map(task => (
                    <div
                      key={task.id}
                      className={styles.taskCard}
                      draggable
                      onDragStart={(e) => handleDragStart(e, task)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleEditTask(task)}
                      style={{ cursor: 'grab' }}
                    >
                      <div className={styles.taskCardInner}>
                        <div className={styles.taskContent}>
                          <div className={styles.taskTitle}>
                            <div className={styles.taskTitleText}>{task.name.replaceAll('_', ' ')}</div>
                          </div>
                          {task.deliverObjectId && phaseNameMap.get(task.deliverObjectId) && (
                            <div className={styles.taskPhase}>{phaseNameMap.get(task.deliverObjectId)}</div>
                          )}
                          <div className={styles.taskAssignee}>
                            {t('table.assignedLabel')} {task.assignedPerson || t('table.notAssigned')}
                          </div>
                          <div className={styles.taskSummary}>{task.description.replaceAll('_', ' ')}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div 
                className={styles.boardColumn}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, 'done')}
              >
                <div className={styles.columnHeader}>
                  <div className={styles.columnTitle}>{t('status.done')}</div>
                </div>
                <div className={styles.columnContent}>
                  {filteredTaskData.filter(t => t.status === 'done').map(task => (
                    <div
                      key={task.id}
                      className={styles.taskCard}
                      draggable
                      onDragStart={(e) => handleDragStart(e, task)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleEditTask(task)}
                      style={{ cursor: 'grab' }}
                    >
                      <div className={styles.taskCardInner}>
                        <div className={styles.taskContent}>
                          <div className={styles.taskTitle}>
                            <div className={styles.taskTitleText}>{task.name.replaceAll('_', ' ')}</div>
                          </div>
                          {task.deliverObjectId && phaseNameMap.get(task.deliverObjectId) && (
                            <div className={styles.taskPhase}>{phaseNameMap.get(task.deliverObjectId)}</div>
                          )}
                          <div className={styles.taskAssignee}>
                            {t('table.assignedLabel')} {task.assignedPerson || t('table.notAssigned')}
                          </div>
                          <div className={styles.taskSummary}>{task.description.replaceAll('_', ' ')}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <CreateTaskModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onTaskCreated={handleTaskCreated}
        project={project}
      />

      <EditTaskModal
        isOpen={!!selectedTaskForEdit}
        onClose={() => setSelectedTaskForEdit(null)}
        onTaskUpdated={handleTaskUpdated}
        onTaskDeleted={handleTaskDeleted}
        task={selectedTaskForEdit || null}
        phases={phases ?? []}
        projectId={project?.id}
      />
    </>
  );
};

export default Tasks;
